import { createContext, useCallback, useContext, useState } from "react";
import type { Dispatch, PropsWithChildren, SetStateAction } from "react";
import { noop } from "lodash";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { FieldValues, UseFieldArrayReturn } from "react-hook-form";
import { PROPERTIES } from "./constants";

type SIDEBAR_PROPERTIES = (typeof PROPERTIES)[keyof typeof PROPERTIES];

export interface CheckoutTabProviderProps {
  initialSidebar?: SIDEBAR_PROPERTIES;
}

export interface CheckoutTabContextProps {
  badgePropertiesForm?: UseFieldArrayReturn<
    FieldValues,
    "paywall_customization_attributes.badge_properties.badges",
    "id"
  >;
  benefitPropertiesForm?: UseFieldArrayReturn<
    FieldValues,
    "paywall_customization_attributes.benefit_properties.benefits",
    "id"
  >;
  currentSidebar: SIDEBAR_PROPERTIES;
  goBack: () => void;
  goToSidebar: (sidebar: SIDEBAR_PROPERTIES) => void;
  history: SIDEBAR_PROPERTIES[];
  priceCalloutPropertiesForm?: UseFieldArrayReturn<
    FieldValues,
    "paywall_customization_attributes.price_callout_properties.prices",
    "id"
  >;
  reducedPricePropertiesForm?: UseFieldArrayReturn<
    FieldValues,
    "paywall_customization_attributes.reduced_price_properties.prices",
    "id"
  >;
  setHistory?: Dispatch<SetStateAction<string[]>>;
  testimonialPropertiesForm?: UseFieldArrayReturn<
    FieldValues,
    "paywall_customization_attributes.testimonial_properties.testimonials",
    "id"
  >;
}

const CheckoutTabProvider = createContext<CheckoutTabContextProps>({
  currentSidebar: PROPERTIES.DEFAULT,
  history: [PROPERTIES.DEFAULT],
  goBack: noop,
  goToSidebar: noop,
});

CheckoutTabProvider.displayName = "CheckoutTabProvider";

export const useCheckoutTabContext = () => useContext(CheckoutTabProvider);

export const CheckoutTabProviderContext = ({
  initialSidebar = PROPERTIES.DEFAULT,
  children,
}: PropsWithChildren<CheckoutTabProviderProps>) => {
  const { control } = useFormContext();

  const benefitPropertiesForm = useFieldArray({
    control,
    name: "paywall_customization_attributes.benefit_properties.benefits",
    shouldUnregister: false,
  });

  const testimonialPropertiesForm = useFieldArray({
    control,
    name: "paywall_customization_attributes.testimonial_properties.testimonials",
    shouldUnregister: false,
  });

  const badgePropertiesForm = useFieldArray({
    control,
    name: "paywall_customization_attributes.badge_properties.badges",
    shouldUnregister: false,
  });

  const priceCalloutPropertiesForm = useFieldArray({
    control,
    name: "paywall_customization_attributes.price_callout_properties.prices",
    shouldUnregister: false,
  });

  const reducedPricePropertiesForm = useFieldArray({
    control,
    name: "paywall_customization_attributes.reduced_price_properties.prices",
    shouldUnregister: false,
  });

  const [history, setHistory] = useState<SIDEBAR_PROPERTIES[]>([
    initialSidebar,
  ]);

  const currentSidebar = history[history.length - 1];
  const goBack = useCallback(() => {
    if (history.length > 1) {
      setHistory(history.slice(0, -1));
    }
  }, [history, setHistory]);

  const goToSidebar = useCallback(
    (sidebar: SIDEBAR_PROPERTIES) => {
      setHistory([...history, sidebar]);
    },
    [setHistory, history],
  );

  const value = {
    history,
    setHistory,
    currentSidebar,
    goBack,
    goToSidebar,
    testimonialPropertiesForm,
    badgePropertiesForm,
    benefitPropertiesForm,
    priceCalloutPropertiesForm,
    reducedPricePropertiesForm,
  };

  return (
    <CheckoutTabProvider.Provider value={value}>
      {children}
    </CheckoutTabProvider.Provider>
  );
};
